/**
 * Clerk Billing rollout flag — frontend half of the cutover seam.
 *
 * Mirrors `stagery-api`'s `USE_CLERK_BILLING` env (PR #149 / 10-07a). Both must
 * stay in sync; flip backend first, then frontend.
 *
 * `off` is the safe default — renders legacy `<SubscriptionPage />` and hides
 * Clerk's `<PricingTable />` until Sam has finished the dashboard config and
 * the 14-day parallel-run burn-in is green.
 */

export type ClerkBillingMode = 'off' | 'new_signups_only' | 'all';

const VALID: readonly ClerkBillingMode[] = ['off', 'new_signups_only', 'all'] as const;

/**
 * Resolve `VITE_USE_CLERK_BILLING` from `import.meta.env`. Garbage values fall
 * back to `'off'` (with a console warn in dev) so we never accidentally flip
 * the cutover on a typo.
 */
/**
 * Read the raw flag value. Vite's prod build inlines `import.meta.env.VITE_*`
 * at build time, so this is effectively a constant in production. In tests
 * (vitest 4) we fall back to `process.env` so `vi.stubEnv` works.
 *
 * Exported for test override only — production callers should use the public
 * helpers below.
 *
 * @internal
 */
export function _readRawFlag(): string {
  const im = (import.meta as ImportMeta & { env?: Record<string, string | boolean> }).env ?? {};
  const fromImport = im.VITE_USE_CLERK_BILLING;
  if (typeof fromImport === 'string' && fromImport.length > 0) return fromImport;
  // vitest path — `import.meta.env.VITE_*` is undefined under jsdom; stubEnv writes process.env.
  if (typeof process !== 'undefined' && process.env?.VITE_USE_CLERK_BILLING) {
    return process.env.VITE_USE_CLERK_BILLING;
  }
  return 'off';
}

function _readDev(): boolean {
  const im = (import.meta as ImportMeta & { env?: Record<string, string | boolean> }).env ?? {};
  if (typeof im.DEV === 'boolean') return im.DEV;
  // Vitest sets NODE_ENV=test which is fine — treat as non-prod for warn.
  return typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production';
}

export function getClerkBillingMode(): ClerkBillingMode {
  const raw = _readRawFlag();
  const normalised = raw.trim().toLowerCase();
  if ((VALID as readonly string[]).includes(normalised)) {
    return normalised as ClerkBillingMode;
  }
  if (_readDev()) {
    // eslint-disable-next-line no-console
    console.warn(
      `[billing] VITE_USE_CLERK_BILLING="${raw}" is not one of ${VALID.join(', ')}; defaulting to 'off'.`,
    );
  }
  return 'off';
}

/**
 * Should the Clerk `<PricingTable />` UI render at all? `false` when the flag
 * is `off` — caller should fall back to the legacy `<SubscriptionPage />`.
 */
export function shouldRenderClerkPricingTable(): boolean {
  return getClerkBillingMode() !== 'off';
}

/**
 * Should we trust `useAuth().has({ feature })` for entitlement gating? Same
 * answer as `shouldRenderClerkPricingTable` today — kept as a separate helper
 * so the gate-source can diverge from the pricing-UI rollout if needed.
 */
export function shouldUseClerkFeatures(): boolean {
  return getClerkBillingMode() !== 'off';
}
